import { useApiCommunicator } from '@btcdirect/shared/src/composables/api/communicator';
import { Endpoints } from '@btcdirect/shared/src/composables/api/endpoints';
import { ApiRequestData, InitializationStates } from '@btcdirect/shared/src/composables/api/types';
import { useInterval } from '@btcdirect/shared/src/composables/util/interval';
import { usePartnerInfo } from '@btcdirect/shared/src/composables/util/partner-info';
import { IbanAccount, UserInfo, useClientInfoStore, useCurrenciesStore, useOfferStore, useUserInfoStore, useWalletAddressesStore } from '@btcdirect/stores';
import { computed, ref } from 'vue';

const initialized = ref<InitializationStates>(InitializationStates.NOT_INITIALIZED);

const { start, stop } = useInterval();

const TIMER = 'new-iban-account';

export const useUserInfo = () => {
    const userInfoStore = useUserInfoStore();

    const get = async (ignoreInitialized: boolean = false): Promise<void> => {
        if (!ignoreInitialized && InitializationStates.INITIALIZED === initialized.value && !!userInfoStore.user?.emailAddress) {
            return;
        }

        await retrieveUserInfo();
    };

    const pollNewIbanAccount = async (): Promise<void> => {
        const initialVerifiedAccounts = filterVerifiedAccounts([...userInfoStore.ibans]);

        await retrieveUserInfo(false);

        const verifiedAccounts = filterVerifiedAccounts(userInfoStore.ibans);
        if (!verifiedAccounts) {
            return;
        }

        if (initialVerifiedAccounts.length === verifiedAccounts.length) {
            return;
        }

        if (!verifiedAccounts.some((account) => !!account.verified && !initialVerifiedAccounts.some((existing) => existing.number === account.number))) {
            return;
        }

        stop(TIMER);
    };

    return {
        initialized: computed<InitializationStates>(() => initialized.value),
        retrieveUserInfo: get,
        startPollingNewIbanAccount: () => start(TIMER, 5000, pollNewIbanAccount)
    };
};

/**
 * Get the user's info from the API.
 */
async function retrieveUserInfo(emptyOnException: boolean = true): Promise<void> {
    const { get } = useApiCommunicator(Endpoints.USER_INFO);
    const userInfoStore = useUserInfoStore();
    const walletAddressesStore = useWalletAddressesStore();
    const clientInfoStore = useClientInfoStore();
    const offerStore = useOfferStore();
    const currenciesStore = useCurrenciesStore();

    initialized.value = InitializationStates.INITIALIZING;

    const { id: partnerId } = usePartnerInfo();
    const payload: ApiRequestData = !!partnerId.value ? { apiClientId: partnerId.value } : null;

    try {
        const response: UserInfo = await get(payload);
        userInfoStore.setUserInfo(response);

        // When wallet verification is not required, we expect the partner to provide the wallet addresses
        if (!clientInfoStore.client.walletVerificationRequired || !response.enabledWalletAddresses?.length) {
            return;
        }

        // If fixed currency is set, only add the wallet addresses for that currency
        const usableWallets = offerStore.offerRequest?.fixedCurrency
            ? response.enabledWalletAddresses.filter((address) => address.currency === currenciesStore.selectedCryptoCurrency.code)
            : response.enabledWalletAddresses;

        walletAddressesStore.addWalletAddresses(usableWallets);
    } catch (exception) {
        if (emptyOnException) {
            userInfoStore.setUserInfo(null);
        }

        return Promise.reject(exception);
    } finally {
        initialized.value = InitializationStates.INITIALIZED;
    }
}

function filterVerifiedAccounts(accounts: IbanAccount[]): IbanAccount[] {
    return accounts.filter((account) => !!account.verified);
}
